import { connect } from 'react-redux';
import { AppState } from '../../../../redux/reducers';
import {
  Consultorio,
  CreateSector,
  CrearLlamadoRequest,
  QueueDisplayScreen,
  Sector,
  Sede,
  UpdateSector,
} from '../../../../models/Entities/Turnero';
import { ErrorType } from '../../../../models/HttpError';
import * as SedesStore from '../../../../redux/middleware/Turnero/Turnero.Sedes.Middleware';
import * as SectoresStore from '../../../../redux/middleware/Turnero/Turnero.Sectores.Middleware';
import * as ConsultoriosStore from '../../../../redux/middleware/Turnero/Turnero.Consultorios.Middleware';
import * as DisplaysStore from '../../../../redux/middleware/Turnero/Turnero.QueueDisplayScreen.Middleware';
import * as LlamadosStore from '../../../../redux/middleware/Turnero/Turnero.Llamados.Middleware';

/** Banderas de un slice "CommonState" que la pantalla necesita. */
export interface SliceStatus {
  isLoading: boolean;
  loaded: boolean;
  loadFailed: boolean;
  isAdding: boolean;
  addFailed: boolean;
  isUpdating: boolean;
  updateFailed: boolean;
  isDeleting: boolean;
  deleteFailed: boolean;
  error?: ErrorType;
}

export interface StateProps {
  sedes: Sede[];
  sectores: Sector[];
  consultorios: Consultorio[];
  displays: QueueDisplayScreen[];
  sedesStatus: SliceStatus;
  sectoresStatus: SliceStatus;
  consultoriosStatus: SliceStatus & { isUpdatingEstado: boolean; updateEstadoFailed: boolean };
  displaysStatus: SliceStatus;
  llamadoStatus: { isCreating: boolean; createFailed: boolean; error?: ErrorType };
}

export interface DispatchProps {
  GetSedes: () => void;
  CreateSede: (payload: Sede) => void;
  UpdateSede: (id: number, payload: Sede) => void;
  DeleteSede: (id: number) => void;
  GetSectores: () => void;
  CreateSector: (payload: CreateSector) => void;
  UpdateSector: (id: number, payload: UpdateSector) => void;
  DeleteSector: (id: number) => void;
  GetConsultorios: () => void;
  CreateConsultorio: (payload: Consultorio) => void;
  UpdateConsultorio: (id: number, payload: Consultorio) => void;
  DeleteConsultorio: (id: number) => void;
  UpdateEstadoConsultorio: (id: number, estado: string) => void;
  GetQueueDisplayScreens: () => void;
  GetQueueDisplayScreenById: (id: number) => void;
  CreateQueueDisplayScreen: (payload: QueueDisplayScreen) => void;
  UpdateQueueDisplayScreen: (id: number, payload: QueueDisplayScreen) => void;
  DeleteQueueDisplayScreen: (id: number) => void;
  CrearLlamado: (payload: CrearLlamadoRequest) => void;
}

export type Props = StateProps & DispatchProps;

const sliceStatus = (slice: any): SliceStatus => ({
  isLoading: Boolean(slice?.isLoadingAll),
  loaded: Boolean(slice?.successLoadingAll),
  loadFailed: Boolean(slice?.failOnLoadingAll),
  isAdding: Boolean(slice?.isAddingNewOne),
  addFailed: Boolean(slice?.failOnAddingNewOne),
  isUpdating: Boolean(slice?.isUpdatingOne),
  updateFailed: Boolean(slice?.failOnUpdatingOne),
  isDeleting: Boolean(slice?.isDeletingOne),
  deleteFailed: Boolean(slice?.failOnDeletingOne),
  error: slice?.error,
});

const EMPTY: never[] = [];
// Misma referencia mientras el slice no cambie (evita renders y recálculos innecesarios).
const asList = <T,>(value: unknown): T[] => (Array.isArray(value) ? (value as T[]) : EMPTY);

export const mapStateToProps = (state: AppState): StateProps => {
  const turnero: any = state.Turnero;
  return {
    sedes: asList<Sede>(turnero?.Sedes?.list),
    sectores: asList<Sector>(turnero?.Sectores?.list),
    consultorios: asList<Consultorio>(turnero?.Consultorios?.list),
    displays: asList<QueueDisplayScreen>(turnero?.QueueDisplayScreen?.list),
    sedesStatus: sliceStatus(turnero?.Sedes),
    sectoresStatus: sliceStatus(turnero?.Sectores),
    consultoriosStatus: {
      ...sliceStatus(turnero?.Consultorios),
      isUpdatingEstado: Boolean(turnero?.Consultorios?.isUpdatingEstado),
      updateEstadoFailed: Boolean(turnero?.Consultorios?.failOnUpdatingEstado),
    },
    displaysStatus: sliceStatus(turnero?.QueueDisplayScreen),
    llamadoStatus: {
      isCreating: Boolean(turnero?.Llamados?.isCreating),
      createFailed: Boolean(turnero?.Llamados?.failOnCreating),
      error: turnero?.Llamados?.error,
    },
  };
};

export const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  GetSedes: () => dispatch(SedesStore.actionCreators.GetSedes()),
  CreateSede: (payload) => dispatch(SedesStore.actionCreators.CreateSede(payload)),
  UpdateSede: (id, payload) => dispatch(SedesStore.actionCreators.UpdateSede(id, payload)),
  DeleteSede: (id) => dispatch(SedesStore.actionCreators.DeleteSede(id)),
  GetSectores: () => dispatch(SectoresStore.actionCreators.GetSectores()),
  CreateSector: (payload) => dispatch(SectoresStore.actionCreators.CreateSector(payload)),
  UpdateSector: (id, payload) => dispatch(SectoresStore.actionCreators.UpdateSector(id, payload)),
  DeleteSector: (id) => dispatch(SectoresStore.actionCreators.DeleteSector(id)),
  GetConsultorios: () => dispatch(ConsultoriosStore.actionCreators.GetConsultorios()),
  CreateConsultorio: (payload) => dispatch(ConsultoriosStore.actionCreators.CreateConsultorio(payload)),
  UpdateConsultorio: (id, payload) => dispatch(ConsultoriosStore.actionCreators.UpdateConsultorio(id, payload)),
  DeleteConsultorio: (id) => dispatch(ConsultoriosStore.actionCreators.DeleteConsultorio(id)),
  UpdateEstadoConsultorio: (id, estado) => dispatch(ConsultoriosStore.actionCreators.UpdateEstadoConsultorio(id, estado)),
  GetQueueDisplayScreens: () => dispatch(DisplaysStore.actionCreators.GetQueueDisplayScreens()),
  GetQueueDisplayScreenById: (id) => dispatch(DisplaysStore.actionCreators.GetQueueDisplayScreenById(id)),
  CreateQueueDisplayScreen: (payload) => dispatch(DisplaysStore.actionCreators.CreateQueueDisplayScreen(payload)),
  UpdateQueueDisplayScreen: (id, payload) => dispatch(DisplaysStore.actionCreators.UpdateQueueDisplayScreen(id, payload)),
  DeleteQueueDisplayScreen: (id) => dispatch(DisplaysStore.actionCreators.DeleteQueueDisplayScreen(id)),
  CrearLlamado: (payload) => dispatch(LlamadosStore.actionCreators.CrearLlamado(payload)),
});

export const connector = connect(mapStateToProps, mapDispatchToProps);
